/**
 * AdminLayout.jsx
 * Persistent top nav for all admin screens.
 *
 * Desktop (≥768px): horizontal mega-nav with hover dropdowns.
 * Mobile (<768px):  slim bar with hamburger → full-screen drawer.
 *
 * Props: user, onLogout, onSwitchRole, children
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AdminCommandPalette from './AdminCommandPalette';

// ─── Nav sections ───────────────────────────────────────────────────────────────

// Exported as the SINGLE SOURCE OF TRUTH for admin navigation — the mega-menu
// home dashboard imports this so it can never drift / orphan a screen again
// (Patrick 2026-06-13: "/offer-types is NOT wired in the mega menu").
const HL_TIP_CSS = `
.hl-tip{ position:relative; }
.hl-tip .hl-navdesc{
  display:none; position:absolute; left:100%; top:50%; transform:translateY(-50%);
  margin-left:10px; z-index:9999;
  background:#0b1220; color:#cbd5e1; border:1px solid #475569; border-radius:6px;
  padding:8px 11px; font-size:11.5px; line-height:1.45; width:max-content; max-width:320px;
  box-shadow:0 10px 28px rgba(0,0,0,.6); white-space:normal; pointer-events:none;
}
.hl-tip:hover .hl-navdesc{ display:block; }
`;

export const NAV_SECTIONS = [
  {
    key: 'agent',
    icon: '👥',
    label: 'Agent & Venues',
    items: [
      { icon: '📌', label: 'Local Assign',       path: '/local-assign',            desc: 'Assign local photos to venues by GPS' },
      { icon: '📍', label: 'Assignment Mode',  path: '/assignment', desc: 'Map-based venue assignment' },
      { icon: '🗺️', label: 'Quick Onboard',    path: '/assignment?mode=onboard', desc: 'Google Maps onboarding' },
      { icon: '🗺️', label: 'Assignment V2',   path: '/assignment-v2',    desc: 'New Mapbox assignment map' },
      { icon: '🏘️', label: 'Zones Manager',    path: '/zones-manager',    desc: 'View & manage venue zones (AC, seating, noise)' },
      { icon: '🌳', label: 'Venue Taxonomy',    path: '/venue-taxonomy/assign', desc: 'Find venues without a type and assign them' },
      { icon: '🗺️', label: 'Prospecting',      path: '/prospecting',        desc: 'Find & add new venues from Google Maps' },
      { icon: '📋', label: 'Venue Table',      path: '/venue-table',        desc: 'Browse & filter all venues in Neon DB' },
      { icon: '🫁', label: 'Venue Health',     path: '/venue-health',       desc: 'Cross-schema completeness — GPS, OSCP, menu, reviews, photos, payments, visits in one mega table' },
      { icon: '🔍', label: 'Google Manager',   path: '/venue-google-data',  desc: 'Browse venue_google_data — ratings, hours, amenities, payment options' },
      { icon: '🔬', label: 'Enrichment Lab',   path: '/venue-google-enrichment', desc: 'Google API vs Apify — 163 columns side by side comparison' },
      { icon: '🏷️', label: 'Type Assignment',  path: '/venue-type-assignment',   desc: 'Assign HL venue types — table + 🗺️ Map toggle (grey=untyped); click marker → assign' },
      { icon: '🕐', label: 'Venue Hours',       path: '/venue-hours',             desc: 'Edit & confirm venue opening hours' },
      { icon: '📍', label: 'Venue Addresses',  path: '/venue-addresses',         desc: 'Normalize & edit venue addresses' },
    ],
  },
  {
    key: 'activities',
    icon: '🎟️',
    label: 'Activities',
    items: [
      { icon: '🎟️', label: 'Activities Manager', path: '/activities', desc: 'EV-708 — activities + types rich tables; match unmatched → run through the venue pipeline' },
      { icon: '🪝', label: 'Activity Scraper', path: '/activity-scraper', desc: 'EV-1198/1208 — browser-harvested OTA activities (Klook/GetYourGuide) staged in activities.external_candidates. Browse the raw capture: page_text, JSON-LD, photos, GPS, price, operator. Review → import/dedupe.' },
    ],
  },
  {
    key: 'map',
    icon: '🗺️',
    label: 'Map Tools',
    items: [
      { icon: '🎯', label: 'Bulk Pin Align',  path: '/bulk-pin-align',  desc: 'Multi-select pins → snap to a street line → save (full GPS history)' },
      { icon: '📍', label: 'Place Triage',        path: '/place-triage',           desc: 'v70.456 — Google Maps links shared in WhatsApp groups, resolved FREE and awaiting YOUR approval. Nothing auto-creates.' },
      { icon: '📍', label: 'Places Manager', path: '/places-manager', desc: 'AD-786 — standalone PLACES (ATM, playground, basketball, table tennis, volleyball, …): pick a type → map + schema + per-type frontend/admin visibility' },
      { icon: '📍', label: 'GPS Change Log',  path: '/gps-change-log',  desc: 'Audit table — every venue position change with prior/new lat/lng, distance, who, when' },
      { icon: '⬡',  label: 'Boundary Manager', path: '/boundary-manager', desc: 'Draw & edit venue property polygons' },
      { icon: '🗺️', label: 'Type Assign (Map)', path: '/venue-type-assign-map', desc: 'Assign venue types visually on a map' },
      // Patrick 2026-07-30: "put vendor config under map tools for now. NO more
      // orphans and ghosts please." It is a map-behaviour screen (live window,
      // trail lookback, street snapping), so it belongs here, not under Venues.
      { icon: '🛵', label: 'Vendor Trails',    path: '/vendor-trails',    desc: 'AD-1407 — WHERE vendors actually roamed: trail map with gap detection' },
      { icon: '⚙️', label: 'Vendor Settings',  path: '/vendor-config',    desc: 'Tracking behaviour — live window, trail lookback, retention, street snapping (EV-1302)' },
    ],
  },
  {
    key: 'media',
    icon: '🖼️',
    label: 'Media',
    items: [
      { icon: '🖼️', label: 'Media Manager',      path: '/media-manager',      desc: 'Browse & approve photos' },
      { icon: '📥', label: 'Quick Approve',       path: '/quick-approve',      desc: 'Mobile: approve NEW Drive media by venue' },
      { icon: '☁️', label: 'Drive Ingest',        path: '/drive-ingest',       desc: 'Google Drive → S3: folder + start date + test + run' },
      { icon: '📸', label: 'Google Photo Backlog', path: '/google-photo-backlog', desc: 'EV-541 — auto-approve-with-map: Google→S3 venue bubbles → tick → approve + AI' },
      { icon: '🗺️', label: 'Local Photos Map',   path: '/local-photos',       desc: 'Map-select local GPS photos (local_hash_scan backlog) → send to Bulk Upload' },
      { icon: '🧮', label: 'Local Hash Sift',     path: '/local-hash-sift',    desc: 'Sift/dedupe the local_hash_scan photo backlog before upload' },
      { icon: '📸', label: 'Bulk Upload',         path: '/bulk-upload',        desc: 'GPS-grouped bulk photo upload (receives Local Photos Map selections; Local Assign opens from here with params)' },
      { icon: '🖼️', label: 'City Gallery Config', path: '/city-gallery-config', desc: 'AD-784 — choose which AI groups the City Gallery shows' },
      { icon: '♻️', label: 'Media Reclaim',       path: '/media-reclaim',       desc: 'Review + bulk hard-delete reclaim candidates (rejected/backlog/hard) + S3 orphans, with thumbnails' },
    ],
  },
  {
    key: 'schemas',
    icon: '🗂️',
    label: 'Schema Managers',
    items: [
      { icon: '🌳', label: 'Venue Taxonomy',        path: '/venue-taxonomy/schema', desc: 'Category/type tree + assign venue types' },
      { icon: '🔗', label: 'Venue Relation Types', path: '/relation-types',      desc: 'Edit relation vocabulary (sister/partner/inside/referred-by) + public-internal toggle' },
      { icon: '📋', label: 'Event Schema',        path: '/event-schema',        desc: 'Event type taxonomy (ETC1/ETC2/L3)' },
      { icon: '🎛️', label: 'Field Designer',      path: '/field-designer', desc: 'Per-type form fields (R/O/H matrix) + audit + sorted' },
      { icon: '🏅', label: 'Venue Lifecycle',     path: '/venue-lifecycle',     desc: 'Verification levels & state machine' },
      { icon: '📶', label: 'WiFi Display Schema', path: '/wifi-display-schema', desc: 'WiFi display method & location types' },
      { icon: '🏷️', label: 'Media Type Schema',  path: '/media-type-schema',   desc: 'Control public visibility & display groups per type' },
    ],
  },
  {
    key: 'events',
    icon: '📅',
    label: 'Events',
    items: [
      { icon: '📅', label: 'Events Manager',   path: '/events-manager',    desc: 'Create & manage events (Neon)' },
      { icon: '🗓️', label: 'Event Settings',   path: '/event-settings',    desc: 'Recurrence horizon, proximity/check-in, past-event opacity (EV-395)' },
      { icon: '🎟️', label: 'Event Organizers', path: '/event-organizers',  desc: 'Invite organizers + set create/publish rights (EV-29)' },
      { icon: '📺', label: 'Match Screenings', path: '/match-screenings',  desc: 'Assign venues showing World Cup matches (EV-45)' },
      { icon: '➕', label: 'Create Event',     path: '/create-event',      desc: 'Quick event creation form' },
      { icon: '🧠', label: 'Event Intelligence', path: '/event-intelligence', desc: 'Extraction pipeline + cluster review (v68.103)' },
      { icon: '📆', label: 'by_weekday Review', path: '/by-weekday-review', desc: '46 ambiguous Sun=0/Mon=0 events to decide (v68.303)' },
      { icon: '📸', label: 'Social Composer',  path: '/social-composer',   desc: 'AI-drafted Instagram posts (v68.314 M1)' },
      { icon: '🤝', label: 'Hangouts',         path: '/hangouts',          desc: 'EV-1448 — social hangouts moderation: hide/restore ad-hoc meetups + reports queue' },
    ],
  },
  {
    key: 'content',
    icon: '📝',
    label: 'Content',
    items: [
      { icon: '📝', label: 'Contact Form',       path: '/contact-form',            desc: 'Edit the public contact form fields and routing' },
      { icon: '📝', label: 'Notes',            path: '/venue-notes',       desc: 'Field agent notes' },
      { icon: '🌐', label: 'Translations',     path: '/translation-cms',   desc: 'i18n content management' },
      { icon: '🎠', label: 'Carousel Config',  path: '/carousel-config',   desc: 'Consumer home carousel / featured config' },
      { icon: '🌐', label: 'Website Control',  path: '/website-control',   desc: 'EV-1051 — mini-CMS for the marketing website: sections, corporate colours, decisions, settings' },
      { icon: '📊', label: 'Live Stats',       path: '/stats',             desc: 'CON-1589 — every number we publish and the query behind it. Copy uses {{stat:key}}, never a literal' },
      { icon: '🕸️', label: 'Content Studio',   path: '/content',           desc: 'CON-1566 — one place to see, search, filter and wire all website + KB + glossary content. The content GRAPH: sections, articles, glossary terms and media, plus the links between them. Attach one screenshot to many pages.' },
      { icon: '🎤', label: 'Pitch Deck Studio', path: '/pitch',            desc: 'CON-1610 — build the pitch deck: publish/unpublish slides from a library, live WYSIWYG preview, swap themes, drag-drop artwork, QR on every slide. Rehearse mode puts the transcript on your phone while a second screen shows the slides. ADMIN ONLY — embargoed until the event.' },
      { icon: '📖', label: 'Rehearsal Script', path: '/pitch-script',   desc: 'CON-1663 — the speaker notes as a phone teleprompter. One slide per screen at readable size, a clock per slide against its own budget so you can see WHICH slide overruns, swipe or arrow to move, screen stays awake. Private: notes never leave the login gate, unlike /pitch-live.' },
      { icon: '📚', label: 'Knowledge Base',   path: '/knowledge-base',    desc: 'CON-1512 — FAQ + knowledge base: search, write, translate and publish. Separate from the landing-page CMS.' },
      { icon: '🗂️', label: 'KB Topics',        path: '/knowledge-base-topics', desc: 'CON-1523 — the knowledge-base topic tree: create, rename, re-order and delete topics (delete guarded while articles still use one).' },
      { icon: '📰', label: 'News',             path: '/news',              desc: 'CON-1504 — the News feed on honestlee.app/news/: write, translate, pick venue photos, publish. Venue and past-event posts arrive here as drafts from the generators.' },
      { icon: '🔗', label: 'Lee Mappings',     path: '/loyalty/mappings',  desc: 'CON-525 — the matching table: which venue category carries which Lee (bind / unbind directly).' },
    ],
  },
  {
    key: 'reviews',
    icon: '⭐',
    label: 'Reviews & AI',
    items: [
      { icon: '🤖', label: 'AI Prompts',         path: '/classification-prompt',  desc: 'Classification rules & testing' },
      { icon: '🔍', label: 'WiFi OCR Prompts',   path: '/wifi-ocr-prompts',       desc: 'Edit WiFi credential extraction prompts with history' },
      { icon: '⭐', label: 'Google Reviews',     path: '/google-reviews',         desc: 'Review intelligence scraper & keyword analysis' },
      { icon: '⭐', label: 'Moderation',       path: '/reviews-manager',   desc: 'Browse, approve, flag & reply to venue reviews' },
      { icon: '⚙️', label: 'Review Config',    path: '/review-config',     desc: 'Facet definitions per venue category + weight formula' },
    ],
  },
  {
    key: 'wifi',
    icon: '📶',
    label: 'WiFi & Noise',
    items: [
      { icon: '📶', label: 'Speed Tests',        path: '/wifi-tests',          desc: 'All speed tests + adhoc tester' },
      { icon: '🌐', label: 'ASN Registry',       path: '/asn-registry',        desc: '77k+ ASNs, brand names, ISP type tags' },
      { icon: '📶', label: 'WiFi Networks',      path: '/wifi-networks',       desc: 'SSIDs, passwords, display methods per venue' },
      { icon: '📜', label: 'Networks Audit Log', path: '/system/wifi-networks-audit', desc: 'Full change history per network — INSERT / UPDATE / soft_delete / restore from DB triggers (v66.161)' },
      { icon: '🔌', label: 'Connections',        path: '/wifi-connections',    desc: 'WPS-detected router model + connect events per session' },
      { icon: '🚪', label: 'PWA Gate Config',    path: '/wifi-gate-config',    desc: 'Consumer map "Connect to WiFi" GPS proximity + iOS/Android behaviour' },
      { icon: '🔥', label: 'Signal Heatmap',     path: '/system/wifi-signal-heatmap', desc: 'Per-AP RSSI heatmap from APK agent-mode collector (v67.146) — Mapbox per-BSSID overlays' },
      // Noise merged under WiFi & Noise (Patrick 2026-06-13) — was its own section.
      { icon: '🔊', label: 'Noise Levels',       path: '/noise-admin',         desc: 'Browse ambient noise measurements & venue profiles' },
    ],
  },
  {
    key: 'grab',
    icon: '🍜',
    label: 'Grab & Menus',
    items: [
      { icon: '🍜', label: 'Grab Scraper',    path: '/grab-scraper',   desc: 'Harvested Grab restaurant URLs + scrape status (VN)' },
      { icon: '🎯', label: 'Grab Matching',   path: '/grab-matching',  desc: 'Match scraped Grab merchants to HonestLee venues' },
      { icon: '🔎', label: 'Venue → Grab Lookup', path: '/venue-grab-lookup', desc: 'For each HL venue, see its Grab coverage + search Grab by name' },
      { icon: '🍽️', label: 'Menu Manager',    path: '/menu-manager',   desc: 'OCR menu extraction & editing' },
      { icon: '🔍', label: 'Dish Search',     path: '/dish-search',    desc: 'Search any dish across all Grab restaurants with prices' },
      { icon: '🍲', label: 'Canonical Dishes', path: '/canonical-dishes', desc: 'EV-528 — master dish catalog (seed + 238k scraped-name pool): review, approve, edit the tree' },
      { icon: '📋', label: 'Canonical Dish Table', path: '/canonical-dish-table', desc: 'EV-528 — flat MEGA table: photos, origin (culinary-research seed vs grab-pool), dietary flags, matched counts, dates' },
      { icon: '🍱', label: 'Unified Menus', path: '/unified-menus', desc: 'EV-529 — every menu in the NEW consolidated schema: source, status (google-structural = draft/hidden), item counts, venue-less grab menus' },
      { icon: '📊', label: 'Dish Stats',      path: '/dish-stats',     desc: 'Most popular dishes by frequency across all menus' },
      { icon: '🖼️', label: 'Grab Media',     path: '/grab-media',     desc: 'Browse all Grab item photos with dedup & cluster visualisation' },
    ],
  },
  {
    key: 'comms',
    icon: '💬',
    label: 'Comms / CRM',
    items: [
      { icon: '💬', label: 'WhatsApp Monitor',   path: '/comms/whatsapp-monitor',  desc: 'Live Baileys monitor — connection, groups, throughput' },
      { icon: '🚀', label: 'Campaign Board',      path: '/campaign',               desc: 'v69.947 — event execution board. Travel Massive Asia 2026 + HorecFex (20-22 Aug, Main Stage pitch Fri 21 Aug 13:00): Kanban by workstream + Gantt timeline + outreach targets (CRM-deduped, auto-tagged) + pgvector knowledge base + agent activity feed.' },
      { icon: '🗂', label: 'CRM',                 path: '/crm',                    desc: 'EV-1080 — THE contact system: 👤 Contacts · 🏢 Companies · 📊 Pipeline · 📥 Inbox tabs. ✨ AI intake, 📇 card scan, 🔎 find-info, WhatsApp linking, Google export.' },
      { icon: '📥', label: 'Unified Inbox',       path: '/inbox',                  desc: 'EV-1231 — ONE inbox across every channel (💬 WhatsApp · 💙 Zalo · ✉️ Email · 🟢 Line) with a channel logo per row, per-channel filter tabs, and 2-way send.' },
      { icon: '🔌', label: 'API Connections',     path: '/api-connections',        desc: 'CON-1619 — every integration and whether it is actually working: Google (contacts/Gmail/Calendar scopes AND whether the API is enabled), Zalo monitor + outbox + pending links, OpenRouter, and the job pipeline. Plus appointment approvals, the automated email templates, and which calendar bookings are written to. Built because three failures in one day were each found by hitting a wall rather than by anything reporting them.' },
      { icon: '💼', label: 'Job Applications',    path: '/job-applications',       desc: 'CON-1568 — applications from honestlee.app/jobs, sorted by the AI score. Verdict (🟢/🟡/🔴), English, both-event-days, phone + whether it is on Zalo/WhatsApp, CV, and the drafted reply. The Unified Inbox filters by channel only, so applications were unfindable there.' },
      { icon: '💙', label: 'Zalo CRM',            path: '/zalo',                   desc: 'EV-1209 — curate your personal-account Zalo contacts (street sellers, cafés, suppliers, venue owners…) + chats. Scraped read-only via zca-js, keyed on the portable zalo_uid. Contacts · Inbox · Groups.' },
      { icon: '💚', label: 'LINE Inbox',          path: '/admin/line-inbox',       desc: 'Thailand venue owner conversations & AI replies' },
      { icon: '📱', label: 'WhatsApp Intelligence', path: '/comms/whatsapp',          desc: 'Messages, events, contacts, groups, media — all in one' },
      { icon: '📘', label: 'Facebook',         path: '/comms/facebook',         desc: 'Facebook intelligence — feed, posts, groups, places, events, media. Sub-tabs deeplinked via URL.' },
    ],
  },
  {
    key: 'users',
    icon: '🔐',
    label: 'Users & Access',
    items: [
          { icon: '📊', label: 'Visitor Analytics', path: '/analytics', desc: 'In-house stats — pageviews, visitors, sources, live now + 👣 Visit History tab' },
      { icon: '👤', label: 'User Manager',     path: '/users', desc: 'Roles, access control & invites' },
      { icon: '🔐', label: 'Rights & Permissions', path: '/permissions', desc: 'FIELD-1626 — granular per-person rights. Give a field agent everything except deleting contacts, photos, notes or old field data.' },
      { icon: '📍', label: 'Agent Visits',     path: '/agent-visits', desc: 'Field agent check-in & visit log' },
      { icon: '🔄', label: 'Legacy Roles Ref',  path: '/legacy-roles', desc: 'Old role system (reference only)' },
    ],
  },
  {
    key: 'loyalty',
    icon: '🦊',
    label: 'Loyalty & Gamification',
    items: [
      { icon: '🏆', label: 'Dashboard',          path: '/gamification',          desc: 'Live economy overview — points, leaderboards, live event feed (EV-603)' },
      { icon: '⚙️', label: 'Settings',           path: '/gamification-settings', desc: 'Edit actions, levels, venue tiers, badges, downline rates (EV-603)' },
      { icon: '🦊', label: 'Loyalty Panel',   path: '/loyalty',          desc: 'CON-525 — Collect the Lees: programs, stamps ledger, users, rewards, redemptions, fraud queue, global settings (card size, cooldowns, GOLD tier).' },
      { icon: '🏷️', label: 'Lee Stamp Tree',  path: '/loyalty/stickers', desc: '#1068 — Lee stamp artwork per venue-taxonomy node, inherited down with overrides; upload the square stamp + default min-spend per category/type.' },
      { icon: '🐻', label: 'Lees Catalog',    path: '/loyalty/lees',     desc: 'CON-525 — the Notion Lee character universe (115 imported): review suggested taxonomy mappings, promote a Lee into a live album sticker, reject the rest.' },
    ],
  },
  {
    key: 'system',
    icon: '⚙️',
    label: 'System',
    items: [
      { icon: '📊', label: 'Dashboard KPI',   path: '/bi-dashboard',    desc: 'Venue & user stats overview' },
      { icon: '📋', label: 'Backlog',          path: '/backlog',         desc: 'Project backlog — single source of truth (Neon project.backlog). Filter, add, update status.' },
      { icon: '📚', label: 'KB Browser',       path: '/kb',              desc: 'AD-1453 — read + search the whole ~/projects/kb markdown tree (checkpoints, manual logs, plans) from anywhere. 15-min Neon mirror.' },
      { icon: '🧩', label: 'Feature Parity',   path: '/feature-parity',  desc: 'EV-931 — cross-surface coverage matrix: PWA vs Native APK (and Admin/Field) per feature. Filter Native=missing to see what is not in the APK yet.' },
      { icon: '🖼️', label: 'Image Matrix',     path: '/image-matrix',    desc: 'EV-1053 — cross-surface image-resize matrix: what image size renders where (PWA/Native/Admin/Field) + via Lambda preset vs RAW. Lookup only for now.' },
      { icon: '🔐', label: 'SSL Monitor',      path: '/ssl-monitor',     desc: 'EV-1177 — TLS cert + domain expiry monitor for every HonestLee host. Daily probe + email alerts at 30/14/7/3/1 days and on failure.' },
      { icon: '⏱', label: 'Cron Jobs',        path: '/cron-overview',   desc: 'Scheduled jobs, run history, manual trigger' },
      { icon: '🚀', label: 'Deploy',           path: '/deploy',          desc: 'Trigger deploys, PM2 status, health checks' },
      { icon: '🎛️', label: 'Ranking Config',   path: '/ranking-config',  desc: 'Map visibility, sort weights, WiFi tiers, search tuning' },
      { icon: '🔘', label: 'Filter Control',    path: '/filter-control-center', desc: 'Consumer map filters — Google JSONB attributes, enable/disable, UI config' },
      { icon: '🧬', label: 'Facet Taxonomy',   path: '/facet-taxonomy',        desc: 'Venue facet taxonomy registry (admin.facet_registry) — 541 facets, approve/reject/edit, sort+filter by type/dimension/provenance' },
      { icon: '📍', label: 'Map Settings',      path: '/map-settings',          desc: 'Map marker appearance — icons, circles, labels, zoom thresholds' },
      { icon: '🔗', label: 'OG Share Templates', path: '/system/og-templates',  desc: 'Link-preview cards (WhatsApp/iMessage/FB) — title, description, image per route + region + language' },
      { icon: '📈', label: 'Code Stats',         path: '/system/code-stats',    desc: 'Daily cloc snapshots — lines of code per app × language over time, with filters' },
      { icon: '🤝', label: 'TravelThru Plan',    path: '/system/travelthru-plan', desc: 'HonestLee × TravelThru Vietnam partnership master plan — rich view of the living KB canvas' },
    ],
  },
  // Comms items moved to AI/ML section
  {
    // Offers pulled out of Commerce into its OWN header (Patrick 2026-06-13).
    key: 'offers',
    icon: '🎁',
    label: 'Offers',
    items: [
      { icon: '🏷', label: 'Offer Types',        path: '/offer-types',             desc: 'Offer taxonomy — the schema behind every offer' },
      { icon: '🎁', label: 'Offers Manager',     path: '/offers',                desc: 'The list of live offers — create/edit, value per kind, status, redemptions (EV-524)' },
      { icon: '🏷️', label: 'Offer Types',        path: '/offers/types',          desc: 'The 28 offer-type definitions + their flags & redemption modes (EV-524)' },
      { icon: '🎟️', label: 'Redemption Modes',   path: '/offers/modes',          desc: 'The 12 redemption modes (remote/staff/payment/connectivity) (EV-524)' },
      { icon: '🔗', label: 'Type × Redemption',  path: '/offers/type-redemption', desc: 'Which redemption modes apply to each offer type (EV-524)' },
      { icon: '🧩', label: 'Venue-Type Matrix',  path: '/offers/venue-matrix',   desc: 'Which offer types suit which business types (EV-524)' },
    ],
  },
  {
    key: 'commerce',
    icon: '💳',
    label: 'Commerce',
    items: [
      { icon: '💳', label: 'Payments',           path: '/admin-payments',  desc: 'Methods · Orders · Institutions — tabbed hub (venue payment methods, pay-at-table orders, VietQR bank directory)' },
      { icon: '📊', label: 'Payment Rates',      path: '/payment-rates',   desc: 'Venue card-surcharge % from agent notes — country/city filters (EV-1160)' },
      { icon: '🏧', label: 'ATM Manager',        path: '/atms',            desc: 'Tracked ATMs from photo GPS — banks, fees, foreign-card support (EV-205)' },
      { icon: '🛒', label: 'Street Vendors',     path: '/street-vendors',  desc: 'Vendor tracking & approval (backlog)' },
      { icon: '🔗', label: 'Referrals',          path: '/referrals',       desc: 'Tracked share links — opens, conversions, top sharers, top venues' },
      { icon: '💰', label: 'Affiliates',          path: '/monetization',    desc: 'Affiliate partner config + revenue tracking (EV-489) — Klook, Booking, eSIM, insurance deep-links' },
    ],
  },

  {
    key: 'qr',
    icon: '🔳',
    label: 'QR Management',
    items: [
      { icon: '🔳', label: 'hlee.app QR Codes',  path: '/hlee-qr',          desc: 'Create / list / revoke / print batches — original combined screen' },
      { icon: '📊', label: 'Codes Mega Table',   path: '/hlee-codes-table', desc: 'Full-schema mega table: 40+ columns, draggable + sortable + pinnable + persisted prefs' },
      { icon: '📡', label: 'Scan Tracking',      path: '/hlee-scans',       desc: 'Every hlee.app scan — where it came from, where it went, who it was. Failed scans surface bad printed stock.' },
    ],
  },
];

const SECTION_COLORS = {
  agent:   { from: '#1d4ed8', to: '#1e40af', accent: '#60a5fa' },
  map:     { from: '#0d9488', to: '#0f766e', accent: '#5eead4' },
  media:   { from: '#0e7490', to: '#164e63', accent: '#22d3ee' },
  analytics:{ from: '#7c3aed', to: '#5b21b6', accent: '#a78bfa' },   // v68.384 — was MISSING → crashed the whole nav
  schemas: { from: '#059669', to: '#065f46', accent: '#4ade80' },
  events:  { from: '#b45309', to: '#92400e', accent: '#fbbf24' },
  content: { from: '#b45309', to: '#78350f', accent: '#fbbf24' },
  reviews: { from: '#b45309', to: '#92400e', accent: '#fbbf24' },
  wifi:    { from: '#7c3aed', to: '#4c1d95', accent: '#a78bfa' },
  noise:   { from: '#be123c', to: '#9f1239', accent: '#fb7185' },
  ai:      { from: '#be185d', to: '#831843', accent: '#f472b6' },
  grab:    { from: '#15803d', to: '#14532d', accent: '#4ade80' },
  users:   { from: '#0369a1', to: '#075985', accent: '#38bdf8' },
  system:  { from: '#374151', to: '#1f2937', accent: '#9ca3af' },
  comms:   { from: '#06C755', to: '#00B900', accent: '#4ade80' },
  commerce:{ from: '#9f1239', to: '#881337', accent: '#fb7185' },
  gamification: { from: '#78350f', to: '#92400e', accent: '#fbbf24' },
  bi:      { from: '#374151', to: '#1f2937', accent: '#6b7280' },
  google:  { from: '#166534', to: '#14532d', accent: '#4ade80' },
  qr:      { from: '#1e3a8a', to: '#0c1d4d', accent: '#7dd3fc' },
};

// ─── Helpers ───────────────────────────────────────────────────────────────────

function initials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function useIsMobile() {
  // 1380px breakpoint: the mega-nav needs ~1500px to fit all sections without overflow.
  // Screens below 1380px get the mobile drawer nav instead.
  const [mobile, setMobile] = useState(() => window.innerWidth < 1380);
  useEffect(() => {
    const check = () => setMobile(window.innerWidth < 1380);
    // orientationchange fires before innerWidth updates on Android tablets;
    // 150ms delay lets the browser settle to the new dimensions first.
    const handleOrient = () => setTimeout(check, 150);
    window.addEventListener('resize', check);
    window.addEventListener('orientationchange', handleOrient);
    return () => {
      window.removeEventListener('resize', check);
      window.removeEventListener('orientationchange', handleOrient);
    };
  }, []);
  return mobile;
}

// ─── User avatar (shared) ──────────────────────────────────────────────────────

const REGIONS = [
  { code: 'th', flag: '🇹🇭', label: 'Thailand' },
  { code: 'my', flag: '🇲🇾', label: 'Malaysia' },
  { code: 'vn', flag: '🇻🇳', label: 'Vietnam' },
  { code: 'id', flag: '🇮🇩', label: 'Indonesia' },
];

function UserAvatar({ user, onLogout, onSwitchRole }) {
  const [open, setOpen] = useState(false);
  const [activeRegion, setActiveRegion] = useState(() => localStorage.getItem('user_region') || 'vn');
  const ref = useRef(null);

  const handleRegionChange = (code) => {
    localStorage.setItem('user_region', code);
    setActiveRegion(code);
    // Force page reload so all components pick up new region
    window.location.reload();
  };

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const roleColor  = user?.role === 'ADMIN' ? '#60a5fa' : '#4ade80';
  const targetRole = user?.role === 'ADMIN' ? 'Agent' : 'Admin';
  const abbr       = initials(user?.name || user?.email || '?');
  const avatarBg   = user?.role === 'ADMIN'
    ? 'linear-gradient(135deg, #1d4ed8, #0e7490)'
    : 'linear-gradient(135deg, #059669, #0369a1)';
  const activeFlag = REGIONS.find(r => r.code === activeRegion)?.flag || '🌐';

  return (
    <div ref={ref} style={{ position: 'relative', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 5 }}>
      <style>{HL_TIP_CSS}</style>
      {/* Always-visible region flag badge */}
      <div
        title={`Active region: ${activeRegion.toUpperCase()} — click avatar to change`}
        style={{
          fontSize: 16, lineHeight: 1, cursor: 'default',
          filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))',
        }}
      >{activeFlag}</div>
      <button
        onClick={() => setOpen(o => !o)}
        title={user?.name || user?.email}
        style={{
          width: 30, height: 30, borderRadius: '50%',
          background: avatarBg,
          border: `2px solid ${open ? roleColor : '#334155'}`,
          color: '#fff', fontSize: 11, fontWeight: 700,
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'border-color .15s', flexShrink: 0,
        }}
      >
        {abbr}
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', right: 0,
          background: '#1e293b', border: '1px solid #334155',
          borderRadius: 10, minWidth: 200,
          boxShadow: '0 8px 32px rgba(0,0,0,.5)',
          zIndex: 1000, overflow: 'hidden',
        }}>
          <div style={{ padding: '12px 14px', borderBottom: '1px solid #334155' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <div style={{
                width: 26, height: 26, borderRadius: '50%',
                background: avatarBg, color: '#fff',
                fontSize: 10, fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>{abbr}</div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0' }}>{user?.name || '—'}</div>
                <div style={{ fontSize: 11, color: '#64748b' }}>{user?.email}</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 10, background: roleColor + '22', color: roleColor, borderRadius: 4, padding: '2px 7px', fontWeight: 700 }}>
                {user?.role}
              </span>
              <span style={{ fontSize: 10, background: '#0f172a', color: '#94a3b8', borderRadius: 4, padding: '2px 7px', fontWeight: 700, border: '1px solid #334155' }}>
                {activeFlag} {activeRegion.toUpperCase()}
              </span>
            </div>
          </div>
          {/* ── Region picker ── */}
          <div style={{ padding: '10px 14px', borderBottom: '1px solid #334155' }}>
            <div style={{ fontSize: 10, color: '#64748b', fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Active Region
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {REGIONS.map(r => (
                <button
                  key={r.code}
                  onClick={() => handleRegionChange(r.code)}
                  title={r.label}
                  style={{
                    padding: '3px 8px', borderRadius: 6, border: 'none', cursor: 'pointer',
                    fontSize: 13, fontWeight: activeRegion === r.code ? 700 : 400,
                    background: activeRegion === r.code ? '#1d4ed8' : '#1e293b',
                    color: activeRegion === r.code ? '#fff' : '#94a3b8',
                    outline: activeRegion === r.code ? '2px solid #60a5fa' : '1px solid #334155',
                    transition: 'all .12s',
                  }}
                >
                  {r.flag} {r.code.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
          {onSwitchRole && (
            <AvatarMenuItem icon="🔄" label={`Switch to ${targetRole} view`} onClick={() => { setOpen(false); onSwitchRole(); }} />
          )}
          <div style={{ height: 1, background: '#334155', margin: '4px 0' }} />
          <AvatarMenuItem icon="🚪" label="Logout" danger onClick={() => { setOpen(false); onLogout(); }} />
        </div>
      )}
    </div>
  );
}

function AvatarMenuItem({ icon, label, onClick, danger }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: '100%', background: hovered ? (danger ? '#450a0a' : '#1e3a5f') : 'none',
        border: 'none', display: 'flex', alignItems: 'center', gap: 9,
        padding: '10px 14px', cursor: 'pointer',
        color: hovered ? (danger ? '#f87171' : '#e2e8f0') : (danger ? '#f87171' : '#94a3b8'),
        fontSize: 13, textAlign: 'left', transition: 'all .15s',
      }}
    >
      <span>{icon}</span>{label}
    </button>
  );
}

// ─── Mobile drawer ─────────────────────────────────────────────────────────────

function MobileDrawer({ open, onClose, navigate, location, user, onLogout, onSwitchRole }) {
  const [expandedKey, setExpandedKey] = useState(null);
  const currentPath = location.pathname;

  // Close drawer on route change
  useEffect(() => { if (open) onClose(); }, [location.pathname]); // eslint-disable-line

  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 500,
        display: 'flex', flexDirection: 'column',
        background: '#0f172a',
        fontFamily: 'Inter, system-ui, sans-serif',
      }}
    >
      {/* Drawer header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 16px', height: 52,
        background: '#1e293b', borderBottom: '1px solid #334155',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <span style={{ fontSize: 18 }}>🎯</span>
          <span style={{ fontWeight: 800, fontSize: 15, color: '#f1f5f9' }}>HonestLee</span>
          <span style={{ fontSize: 10, color: '#475569' }}>Admin</span>
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'rgba(255,255,255,0.06)', border: '1px solid #334155',
            borderRadius: 8, width: 36, height: 36,
            color: '#94a3b8', fontSize: 18, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >✕</button>
      </div>

      {/* Scrollable nav */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
        {/* Home */}
        <button
          onClick={() => navigate('/')}
          style={{
            width: '100%', background: currentPath === '/' ? 'rgba(96,165,250,0.1)' : 'none',
            border: 'none', borderLeft: currentPath === '/' ? '3px solid #60a5fa' : '3px solid transparent',
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '14px 20px', cursor: 'pointer',
            color: currentPath === '/' ? '#60a5fa' : '#e2e8f0',
            fontSize: 15, fontWeight: 600, textAlign: 'left',
          }}
        >
          <span>🏠</span> Home
        </button>

        <div style={{ height: 1, background: '#1e293b', margin: '4px 0' }} />

        {/* Sections */}
        {NAV_SECTIONS.map(section => {
          const colors  = SECTION_COLORS[section.key] || SECTION_COLORS.system;  // v68.384 null-safe
          const isExpanded = expandedKey === section.key;
          const isActive   = section.items.some(i => currentPath === i.path.split('?')[0]);

          return (
            <div key={section.key}>
              {/* Section header — tap to expand */}
              <button
                onClick={() => setExpandedKey(k => k === section.key ? null : section.key)}
                style={{
                  width: '100%', background: isExpanded ? `${colors.from}18` : 'none',
                  border: 'none',
                  borderLeft: isActive ? `3px solid ${colors.accent}` : '3px solid transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '13px 20px', cursor: 'pointer',
                  color: isActive ? colors.accent : '#e2e8f0',
                  fontSize: 14, fontWeight: isActive ? 700 : 500, textAlign: 'left',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 18 }}>{section.icon}</span>
                  <span>{section.label}</span>
                </div>
                <span style={{ fontSize: 11, color: '#475569', transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}>▾</span>
              </button>

              {/* Section items */}
              {isExpanded && (
                <div style={{ background: 'rgba(0,0,0,0.2)', borderBottom: '1px solid #1e293b' }}>
                  {section.items.map(item => {
                    const active = currentPath === item.path.split('?')[0];
                    return (
                      <a
                        key={item.path}
                        href={item.path}
                        onClick={e => { e.preventDefault(); navigate(item.path); }}
                        style={{
                          width: '100%', background: active ? `${colors.from}22` : 'none',
                          borderLeft: active ? `3px solid ${colors.accent}` : '3px solid transparent',
                          display: 'flex', alignItems: 'center', gap: 12,
                          padding: '12px 20px 12px 36px',
                          cursor: 'pointer', textAlign: 'left',
                          textDecoration: 'none', boxSizing: 'border-box',
                        }}
                        className="hl-tip"
                        title={item.desc}
                      >
                        <span style={{ fontSize: 16 }}>{item.icon}</span>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: active ? colors.accent : '#e2e8f0' }}>
                            {item.label}
                          </div>
                        </div>
                        {item.desc && <span className="hl-navdesc">{item.desc}</span>}
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Bottom: user + logout */}
      <div style={{
        borderTop: '1px solid #334155', padding: '12px 16px',
        background: '#1e293b', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0' }}>{user?.name || user?.email || '—'}</div>
          <div style={{ fontSize: 11, color: '#64748b' }}>{user?.role}</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {onSwitchRole && (
            <button
              onClick={() => { onClose(); onSwitchRole(); }}
              style={{
                background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.25)',
                borderRadius: 8, padding: '7px 12px', color: '#60a5fa',
                fontSize: 12, cursor: 'pointer',
              }}
            >🔄 Switch</button>
          )}
          <button
            onClick={() => { onClose(); onLogout(); }}
            style={{
              background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.25)',
              borderRadius: 8, padding: '7px 12px', color: '#f87171',
              fontSize: 12, cursor: 'pointer',
            }}
          >🚪 Logout</button>
        </div>
      </div>
    </div>
  );
}

// ─── Mobile slim bar ───────────────────────────────────────────────────────────

function MobileTopBar({ user, onOpenDrawer, onLogout, onSwitchRole }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '0 14px', height: 52,
      background: '#1e293b', borderBottom: '1px solid #0f172a',
      flexShrink: 0, zIndex: 200,
    }}>
      {/* Logo */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
        <span style={{ fontSize: 18 }}>🎯</span>
        <span style={{ fontWeight: 800, fontSize: 15, color: '#f1f5f9' }}>HonestLee</span>
        <span style={{ fontSize: 10, color: '#475569' }}>Admin</span>
      </div>

      {/* Right: avatar + burger */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <UserAvatar user={user} onLogout={onLogout} onSwitchRole={onSwitchRole} />
        <button
          onClick={onOpenDrawer}
          style={{
            background: 'rgba(255,255,255,0.06)', border: '1px solid #334155',
            borderRadius: 8, width: 38, height: 38,
            color: '#e2e8f0', fontSize: 18, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          aria-label="Open menu"
        >☰</button>
      </div>
    </div>
  );
}

// ─── Desktop top nav (unchanged) ───────────────────────────────────────────────

function DesktopTopNav({ user, onLogout, onSwitchRole, onOpenPalette }) {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;

  const [openKey, setOpenKey] = useState(null);
  const closeTimer = useRef(null);
  const barRef = useRef(null);

  const openSection  = key => { clearTimeout(closeTimer.current); setOpenKey(key); };
  const scheduleClose = () => { closeTimer.current = setTimeout(() => setOpenKey(null), 180); };

  useEffect(() => {
    const h = e => { if (barRef.current && !barRef.current.contains(e.target)) setOpenKey(null); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // Close any open dropdown when screen resizes (e.g. orientation change on tablet)
  useEffect(() => {
    const h = () => setOpenKey(null);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  return (
    <div
      ref={barRef}
      style={{
        display: 'flex', alignItems: 'center', gap: 0,
        background: '#1e293b', borderBottom: '1px solid #0f172a',
        padding: '0 16px', height: 46,
        flexShrink: 0, position: 'relative', zIndex: 200,
        boxShadow: '0 1px 0 #0f172a',
      }}
    >
      {/* Logo */}
      <button
        onClick={() => navigate('/')}
        style={{
          display: 'flex', alignItems: 'center', gap: 7,
          background: 'none', border: 'none', cursor: 'pointer',
          padding: '0 10px 0 0', marginRight: 4, flexShrink: 0,
        }}
      >
        <span style={{ fontSize: 17 }}>🎯</span>
        <span style={{ fontWeight: 800, fontSize: 14, color: '#f1f5f9', letterSpacing: '-.02em' }}>HonestLee</span>
        <span style={{ fontSize: 10, color: '#475569', fontWeight: 500 }}>Admin</span>
      </button>

      <div style={{ width: 1, height: 20, background: '#334155', marginRight: 6, flexShrink: 0 }} />

      {/* Home */}
      <button
        onClick={() => navigate('/')}
        style={{
          background: currentPath === '/' ? '#1e3a8a33' : 'none',
          border: 'none', borderRadius: 6, padding: '4px 9px',
          color: currentPath === '/' ? '#60a5fa' : '#94a3b8',
          cursor: 'pointer', fontSize: 12, fontWeight: 600, flexShrink: 0,
        }}
      >🏠 Home</button>

      <div style={{ width: 1, height: 18, background: '#334155', margin: '0 4px', flexShrink: 0 }} />

      {/* Section buttons */}
      {NAV_SECTIONS.map(section => {
        const colors  = SECTION_COLORS[section.key] || SECTION_COLORS.system;  // v68.384 null-safe
        const isOpen   = openKey === section.key;
        const isActive = !section.comingSoon && section.items.some(i => currentPath === i.path.split('?')[0]);

        return (
          <div
            key={section.key}
            style={{ position: 'relative', flexShrink: 0 }}
            onMouseEnter={() => !section.comingSoon && openSection(section.key)}
            onMouseLeave={scheduleClose}
          >
            <button
              style={{
                background: isOpen ? `${colors.from}22` : (isActive ? `${colors.from}18` : 'none'),
                border: 'none', borderRadius: 6, padding: '4px 7px',
                color: isOpen || isActive ? colors.accent : (section.comingSoon ? '#475569' : '#94a3b8'),
                cursor: section.comingSoon ? 'default' : 'pointer',
                fontSize: 11, fontWeight: 600,
                display: 'flex', alignItems: 'center', gap: 3,
                transition: 'all .15s', whiteSpace: 'nowrap',
              }}
            >
              <span>{section.icon}</span>
              <span>{section.label}</span>
              {section.comingSoon ? (
                <span style={{ fontSize: 8, background: '#334155', color: '#475569', borderRadius: 2, padding: '1px 4px' }}>SOON</span>
              ) : (
                <span style={{ fontSize: 9, color: '#475569' }}>▾</span>
              )}
            </button>

            {isOpen && !section.comingSoon && (
              <div
                onMouseEnter={() => openSection(section.key)}
                onMouseLeave={scheduleClose}
                style={{
                  position: 'absolute', top: '100%', left: 0,
                  background: '#1e293b', border: '1px solid #334155',
                  borderRadius: 10, minWidth: 220, marginTop: 4,
                  boxShadow: '0 12px 40px rgba(0,0,0,.5)',
                  overflow: 'hidden', zIndex: 300,
                }}
              >
                {section.items.map(item => {
                  const active = currentPath === item.path.split('?')[0];
                  return (
                    <a
                      key={item.path}
                      href={item.path}
                      onClick={e => { e.preventDefault(); setOpenKey(null); navigate(item.path); }}
                      style={{
                        width: '100%', background: active ? `${colors.from}22` : 'none',
                        display: 'flex', alignItems: 'flex-start',
                        gap: 10, padding: '10px 14px', cursor: 'pointer',
                        textAlign: 'left', borderLeft: active ? `3px solid ${colors.accent}` : '3px solid transparent',
                        transition: 'all .12s', textDecoration: 'none', boxSizing: 'border-box',
                      }}
                      className="hl-tip"
                      title={item.desc}
                      onMouseEnter={e => { if (!active) e.currentTarget.style.background = '#162032'; }}
                      onMouseLeave={e => { if (!active) e.currentTarget.style.background = active ? `${colors.from}22` : 'none'; }}
                    >
                      <span style={{ fontSize: 15, marginTop: 1 }}>{item.icon}</span>
                      {item.desc && <span className="hl-navdesc">{item.desc}</span>}
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: active ? colors.accent : '#e2e8f0' }}>{item.label}</div>
                      </div>
                    </a>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {/* Search — inside the scrollable area */}
      <button
        onClick={onOpenPalette}
        title="Search (Ctrl+K)"
        style={{
          background: '#0f172a', border: '1px solid #334155',
          borderRadius: 6, padding: '3px 10px',
          color: '#64748b', fontSize: 12, cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 6,
          whiteSpace: 'nowrap', marginLeft: 8, flexShrink: 0,
        }}
      >
        <span>🔍</span>
        <span>Search</span>
        <kbd style={{ fontSize: 9, background: '#1e293b', border: '1px solid #334155', borderRadius: 3, padding: '1px 4px', color: '#475569' }}>Ctrl K</kbd>
      </button>

      <div style={{ flex: 1 }} />

      <UserAvatar user={user} onLogout={onLogout} onSwitchRole={onSwitchRole} />
    </div>
  );
}

// ─── Layout wrapper ────────────────────────────────────────────────────────────

export default function AdminLayout({ user, onLogout, onSwitchRole, children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();

  const [paletteOpen, setPaletteOpen] = useState(false);
  const [drawerOpen,  setDrawerOpen]  = useState(false);

  // Global Ctrl+K / Cmd+K
  useEffect(() => {
    const h = e => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setPaletteOpen(o => !o);
      }
    };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, []);

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: '100vh',
      background: '#0f172a', color: '#e2e8f0',
      fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
    }}>
      {isMobile ? (
        <>
          <MobileTopBar
            user={user}
            onLogout={onLogout}
            onSwitchRole={onSwitchRole}
            onOpenDrawer={() => setDrawerOpen(true)}
          />
          <MobileDrawer
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            navigate={navigate}
            location={location}
            user={user}
            onLogout={onLogout}
            onSwitchRole={onSwitchRole}
          />
        </>
      ) : (
        <DesktopTopNav
          user={user}
          onLogout={onLogout}
          onSwitchRole={onSwitchRole}
          onOpenPalette={() => setPaletteOpen(true)}
        />
      )}

      {/* EV-426 — overflowX:auto so any screen/panel wider than a tablet scrolls
          horizontally instead of breaking the layout (nav already drops to a drawer
          <1380px; DataTable scrolls its own table internally). */}
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'auto', display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0 }}>
        {children}
      </div>

      {!isMobile && (
        <AdminCommandPalette
          isOpen={paletteOpen}
          onClose={() => setPaletteOpen(false)}
          onNavigate={path => { navigate(path); setPaletteOpen(false); }}
        />
      )}
    </div>
  );
}
